import { gql, useMutation } from '@apollo/client';
import { Button, HStack, IconButton, Text, useToast } from '@chakra-ui/react';
import { t } from 'i18next';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { Row } from 'react-table';
import {
  AddIcon,
  DocumentIcon,
  EditPenIcon,
  FailedIcon,
  PendingIcon,
  RefreshIcon,
  SuccessIcon,
  TrashIcon,
} from '~components/ui/icons';
import { SsoProviderStrategy, SsoValidationStatus } from '~graphql/__generated__/types';
import { OrganizationSsoIndexPageDocument } from '~pages/[organization]/settings/sso/__generated__/Index.graphql';
import { useDeleteDomainMutation } from './__generated__/Cells.graphql';
import { SsoTableSsoFragment } from './__generated__/SsoTable.graphql';
import { ChooseSsoServiceModal } from './ChooseSsoServiceModal';
import { DeleteSsoModal } from './DeleteSsoModal';
import { OidcInitialData, OidcRegistrationModal } from './OidcRegistrationModal';
import { SamlInitialData, SamlRegistrationModal } from './SamlRegistrationModal';
import { SsoServiceType } from './SsoTypes';

// SSO Registration cell - shows document icon or create button
export const SsoRegistrationCell = ({
  row: { original: sso },
}: {
  row: Row<SsoTableSsoFragment>;
}) => {
  const [step, setStep] = useState<'idle' | 'choose-service' | 'register'>('idle');
  const [selectedService, setSelectedService] = useState<SsoServiceType | null>(null);
  const [initialData, setInitialData] = useState<OidcInitialData | SamlInitialData | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const handleOpenFlow = () => setStep('choose-service');

  const handleClose = () => {
    setStep('idle');
    setSelectedService(null);
    setInitialData(null);
    setIsEditing(false);
  };

  const handleSelectService = (
    service: SsoServiceType,
    initialData: OidcInitialData | SamlInitialData | null,
  ) => {
    setSelectedService(service);
    setInitialData(initialData);
    setStep('register');
  };

  // Open existing SSO entry for editing - uses data directly from sso prop
  const handleOpenExistingEntry = () => {
    const provider = sso.provider;
    if (!provider) return;

    if (
      provider.strategy === SsoProviderStrategy.Oidc &&
      provider.options.__typename === 'OidcOptions'
    ) {
      setSelectedService(SsoServiceType.OIDC);
      const oidcOptions = provider.options;
      setInitialData({
        connectionName: provider.displayName || '',
        issuerUrl: oidcOptions?.issuer || '',
        clientId: oidcOptions?.clientId || '',
        clientSecret: oidcOptions?.clientSecret || '',
        validationStatus: provider.validationStatus,
      });
    } else if (
      provider.strategy === SsoProviderStrategy.Samlp &&
      provider.options.__typename === 'SamlOptions'
    ) {
      setSelectedService(SsoServiceType.SAML);
      const samlOptions = provider.options;
      setInitialData({
        connectionName: provider.displayName || '',
        entityId: samlOptions.entityId || '',
        signingUrl: samlOptions?.signInEndpoint || '',
        x509Certificate: samlOptions?.signingCert || '',
        validationStatus: provider.validationStatus,
      });
    }

    setIsEditing(true);
    setStep('register');
  };

  // If SSO has been configured, show doc icon with link
  if (sso.provider?.strategy) {
    return (
      <>
        <Button
          variant="link"
          colorScheme="gray"
          leftIcon={<DocumentIcon boxSize="4" />}
          onClick={handleOpenExistingEntry}
          fontSize="sm"
          textDecoration="underline"
        >
          {t('ssoEntry')}
        </Button>

        {step === 'register' && selectedService === SsoServiceType.OIDC && (
          <OidcRegistrationModal
            isOpen={true}
            onClose={handleClose}
            ssoId={sso.id}
            initialData={initialData && 'clientId' in initialData ? initialData : undefined}
            isEditing={isEditing}
          />
        )}

        {step === 'register' && selectedService === SsoServiceType.SAML && (
          <SamlRegistrationModal
            isOpen={true}
            onClose={handleClose}
            ssoId={sso.id}
            initialData={initialData && 'entityId' in initialData ? initialData : undefined}
            isEditing={isEditing}
            domain={sso.domain}
          />
        )}
      </>
    );
  }

  // Otherwise show create button with modal
  return (
    <>
      <Button
        variant="link"
        colorScheme="blue"
        leftIcon={<AddIcon />}
        onClick={handleOpenFlow}
        fontSize="sm"
      >
        {t('createSsoEntry')}
      </Button>

      {step === 'choose-service' && (
        <ChooseSsoServiceModal
          isOpen={true}
          onClose={handleClose}
          onNext={handleSelectService}
          ssoId={sso.id}
          domain={sso.domain}
        />
      )}

      {step === 'register' && selectedService === SsoServiceType.OIDC && (
        <OidcRegistrationModal
          isOpen={true}
          onClose={handleClose}
          ssoId={sso.id}
          initialData={initialData && 'clientId' in initialData ? initialData : undefined}
        />
      )}

      {step === 'register' && selectedService === SsoServiceType.SAML && (
        <SamlRegistrationModal
          isOpen={true}
          onClose={handleClose}
          ssoId={sso.id}
          initialData={initialData && 'entityId' in initialData ? initialData : undefined}
          domain={sso.domain}
        />
      )}
    </>
  );
};

// Actions cell
export const SsoActionsCell = ({ row: { original: sso } }: { row: Row<SsoTableSsoFragment> }) => {
  const toast = useToast();
  const [deleteDomain] = useDeleteDomainMutation({
    refetchQueries: [{ query: OrganizationSsoIndexPageDocument }],
    awaitRefetchQueries: true,
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isSubmitted = Boolean(sso.provider?.validationStatus);

  const [ssoRevalidate, { loading: isRevalidating }] = useMutation(
    SsoRegistrationCell.graphql.mutations.SsoRevalidate,
    {
      refetchQueries: [{ query: OrganizationSsoIndexPageDocument }],
      awaitRefetchQueries: true,
    },
  );

  const handleRefresh = async () => {
    try {
      await ssoRevalidate({
        variables: {
          input: {
            id: sso.id,
          },
        },
      });
      toast({
        title: t('ssoRevalidateSuccess'),
        status: 'success',
      });
    } catch (e) {
      toast({
        title: t('ssoRevalidateError'),
        description: (e as Error).message,
        status: 'error',
      });
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      console.log('Deleting domain', sso.id);
      await deleteDomain({
        variables: {
          input: {
            id: sso.id,
          },
        },
      });
      toast({
        title: (
          <Trans
            i18nKey="domainDeleted"
            values={{ name: sso.provider?.displayName ?? sso.domain }}
            components={{ strong: <strong /> }}
          />
        ),
        status: 'success',
      });
      setIsDeleteModalOpen(false);
    } catch (e) {
      toast({
        title: t('deleteSsoError'),
        description: (e as Error).message,
        status: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <HStack spacing={2} justify="flex-end">
        <IconButton
          aria-label="Refresh"
          icon={<RefreshIcon boxSize="8" />}
          variant="ghost"
          colorScheme="gray"
          size="sm"
          isDisabled={!isSubmitted}
          onClick={handleRefresh}
          isLoading={isRevalidating}
        />
        <IconButton
          aria-label="Delete"
          icon={<TrashIcon boxSize="6" />}
          variant="ghost"
          colorScheme="gray"
          size="sm"
          onClick={handleDeleteClick}
        />
      </HStack>

      {isDeleteModalOpen && (
        <DeleteSsoModal
          isOpen={isDeleteModalOpen}
          onCancel={() => setIsDeleteModalOpen(false)}
          onDelete={handleDeleteConfirm}
          sso={sso}
          isLoading={isDeleting}
        />
      )}
    </>
  );
};

// Helpers for accessors
export const getRegistrationStatusNode = (sso: SsoTableSsoFragment) => {
  if (sso.provider?.validationStatus === SsoValidationStatus.Completed) {
    return (
      <HStack>
        <SuccessIcon boxSize="6" color="green.500" />
        <Text fontWeight="medium" fontSize="sm">
          {t('validated')}
        </Text>
      </HStack>
    );
  }
  if (sso.provider?.validationStatus === SsoValidationStatus.Pending) {
    return (
      <HStack>
        <PendingIcon boxSize="6" color="yellow.600" />
        <Text fontWeight="medium" fontSize="sm">
          {t('validationInProgress')}
        </Text>
      </HStack>
    );
  }
  if (sso.provider?.validationStatus === SsoValidationStatus.Failed) {
    return (
      <HStack>
        <FailedIcon boxSize="6" color="red.500" />
        <Text fontWeight="medium" fontSize="sm">
          {t('failedToValidate')}
        </Text>
      </HStack>
    );
  }
  // When provider exists but validationStatus is null - waiting for user input
  if (sso.provider && sso.provider.validationStatus === null) {
    return (
      <HStack>
        <EditPenIcon boxSize="6" color="blue.500" />
        <Text fontWeight="medium" fontSize="sm">
          {t('waitingForInput')}
        </Text>
      </HStack>
    );
  }
  return null;
};

export const getSsoServiceLabel = (sso: SsoTableSsoFragment) => {
  if (!sso.provider?.strategy) return null;
  const strategyLabel = sso.provider.strategy === SsoProviderStrategy.Oidc ? 'OIDC' : 'SAML';
  return (
    <Text fontWeight="medium" fontSize="sm">
      {strategyLabel}
    </Text>
  );
};

SsoRegistrationCell.graphql = {
  mutations: {
    DeleteDomain: gql`
      mutation DeleteDomain($input: SsoDomainDeleteInput!) {
        ssoDomainDelete(input: $input)
      }
    `,
    SsoRevalidate: gql`
      mutation SsoRevalidate($input: SsoRevalidateInput!) {
        ssoRevalidate(input: $input)
      }
    `,
  },
};
